import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { unAssignUsersToWorkspaceUser } from 'wisestep-api';
import { WsButton, WsCheckboxList } from 'wisestep-base';
import { object, string } from 'yup';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { fetchAndSetAssignedUsers, getUserNameByIdInWorkspaceUsers } from '../helper/hooks';

import { StyledButtonFooter } from './UserAccessList';

let recruitersOptions: {
  [key: string]: string;
} = {};

interface UserAccessAssignedListProps {
  onAssign(): void;
  loggedInUser: string;
  assignedUsers?: string[];
}

const validationSchema = {
  assignedUsersList: string().required('The body cannot be empty'),
};

const UserAccessAssignedList = ({ onAssign, loggedInUser }: UserAccessAssignedListProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [assignedUsersList, setAssignedUsersList] = useState<string[] | undefined>();
  const [userIdToUsernameMap, setUserIdToUsernameMap] = useState<Record<string, string | null>>({});
  const [filteredRecruitersOptions, setFilteredRecruitersOptions] = useState(recruitersOptions);
  const formOptions = {
    resolver: yupResolver(object().shape(validationSchema)),
    defaultValues: { assignedUsersList: [] },
  };
  const methods = useForm(formOptions);
  const { setValue, watch } = methods;

  const fetchData = useCallback(async () => {
    setUserIdToUsernameMap(await getUserNameByIdInWorkspaceUsers());
    setAssignedUsersList(await fetchAndSetAssignedUsers(loggedInUser));
  }, [loggedInUser, setAssignedUsersList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectEvents = (checked: boolean) => {
    checked
      ? setValue('assignedUsersList', Object.keys(recruitersOptions))
      : setValue('assignedUsersList', []);
  };

  const unAssign = async () => {
    const query = {
      roles: [],
      statuses: [],
      userIds: watch('assignedUsersList'),
      page: 0,
      size: 20,
      id: loggedInUser,
    };
    try {
      const response = await unAssignUsersToWorkspaceUser(query);
      if (response.status === 200) {
        setNotification(success('User unassigned Successfully'));
        setAssignedUsersList([]);
        setValue('assignedUsersList', []);
        recruitersOptions = {};
        fetchData();
      }
      onAssign();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const objectFilter = (obj, predicate) =>
    Object.keys(obj)
      .filter((key) => predicate(obj[key]))
      .reduce((res, key) => ((res[key] = obj[key]), res), {});

  const recruitersInput = (event) => {
    if (!isEmpty(event.target.value)) {
      const filtered = objectFilter(recruitersOptions, (row) =>
        row.toLowerCase().includes(event.target.value.toLowerCase())
      );
      setFilteredRecruitersOptions(filtered);
    } else {
      setFilteredRecruitersOptions(recruitersOptions);
    }
  };

  assignedUsersList?.forEach((email) => {
    recruitersOptions[email] = `${userIdToUsernameMap[email]} (${email})`;
  });

  return (
    <Box>
      <FormProvider {...methods}>
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <SearchIcon />
          <InputBase placeholder="Search User" onChange={recruitersInput} fullWidth />
        </Stack>
        <Stack spacing={1}>
          <WsCheckBox
            name="selectAll"
            label="Select All Users"
            bold={true}
            onClick={handleSelectEvents}
          />
          <WsCheckboxList name="assignedUsersList" options={filteredRecruitersOptions} />
        </Stack>
        <StyledButtonFooter container direction="row">
          <WsButton variant="text" size="large">
            {`${
              (methods.watch('assignedUsersList') && methods.watch('assignedUsersList').length) ?? 0
            } Selected`}
          </WsButton>
          <WsButton variant="outlined" size="large" onClick={unAssign}>
            Unassign
          </WsButton>
        </StyledButtonFooter>
      </FormProvider>
    </Box>
  );
};

export default UserAccessAssignedList;
